// Venue-search VS1.3 (VS1.4: the inventory). The venue editor's new facts: how they load from a
// venue, how they are checked, and what is saved. Plain functions, so tests
// can check every saved value. Field IDs, rules and lists come from the field
// dictionary (src/taxonomy); nothing here types a field list of its own.
import { dictionary, fromLegacy, maxLength, regionOf, toLegacy } from '../../../taxonomy';
import { fieldProblems, isAnswered, isShown } from '../../../taxonomy/rules';
import { cleanContact } from '../../sundays';
import { hasIncluded, inventoryProblems, rentalsOffered, rowsFromAmenities, savedInventory } from './inventory';

// The public facts the editor asks for, in page order.
export const CAPACITY_FIELDS = ['capacity.seated.indoor', 'capacity.standing.indoor', 'capacity.seated.outdoor', 'capacity.standing.outdoor'];
export const RESTROOM_FIELDS = ['restrooms.womens', 'restrooms.mens', 'restrooms.singleUser'];
export const FEATURE_FIELDS = [
  'space.rainBackup',
  'climate.heat',
  'climate.airConditioning',
  'space.danceArea',
  'space.meetingRoom',
  'rooms.bridalSuite',
  'rooms.groomsRoom',
  'facilities.pool',
  'access.wheelchair',
  'tech.wifi',
  'tech.screen',
  'power.outlets',
  'power.heavyDuty',
  'lighting.outdoor'
];
export const PARKING_FIELDS = ['parking.onProperty', 'parking.street', 'parking.offSite'];
export const RULE_FIELDS = ['rules.alcohol', 'rules.amplifiedMusic', 'rules.musicEndTime', 'rules.dogs', 'rules.fire'];

export const FACT_FIELDS = [
  'area.indoor',
  'area.outdoor',
  ...CAPACITY_FIELDS,
  ...RESTROOM_FIELDS,
  'kitchen',
  ...FEATURE_FIELDS,
  'lighting.details',
  ...PARKING_FIELDS,
  'parking.total',
  'parking.shuttle',
  ...RULE_FIELDS,
  'rules.eventTypesNotAllowed',
  'overnight.allowed',
  'overnight.beds',
  'overnight.showers',
  'fullDay.start',
  'fullDay.end',
  'hourly.price',
  'hourly.minHours',
  'inventory', // VS1.4
  'rentals.offered' // auto, from the inventory
];

// The square footage helper (Braeden, VS1.3 plan, decision B).
export const SQFT_PER_SEATED = 12;
export const SQFT_PER_STANDING = 8;
const M2_PER_SQFT = 0.09290304;
export const sqftToM2 = (sqft) => sqft * M2_PER_SQFT;
export const m2ToSqft = (m2) => Math.round(m2 / M2_PER_SQFT);
export const estimateFromSqft = (sqft) => ({ seated: Math.floor(sqft / SQFT_PER_SEATED), standing: Math.floor(sqft / SQFT_PER_STANDING) });

// "1,200", "1200 sq ft", "$1,500" -> 1200 / 1500. "" -> undefined (not answered). 0 is an answer.
export const parseCount = (text) => {
  const digits = String(text ?? '').replace(/[^0-9]/g, '');
  return digits === '' ? undefined : Math.min(Number(digits), 9999999);
};

const read = (venue, path) => path.split('.').reduce((value, key) => (value && typeof value === 'object' ? value[key] : undefined), venue);

// A flat map of field ID -> value, from a saved venue.
export const factsFromVenue = (venue) => {
  const facts = {};
  if (!venue) return facts;
  FACT_FIELDS.forEach((id) => {
    const value = read(venue, id);
    if (value !== undefined && value !== null && id !== 'rentals.offered') facts[id] = value;
  });
  // VS1.4 decision B: an old venue with no items starts with the items its old checkboxes named.
  if (facts.inventory === undefined) {
    const rows = rowsFromAmenities(venue.amenities);
    if (rows) facts.inventory = rows;
  }
  return facts;
};

// What the dictionary rules see: the facts, plus the setting as its ID.
export const ruleValues = (form) => ({ ...form.facts, setting: fromLegacy('setting', form.setting) });

const SIDES = { indoor: ['indoor'], outdoor: ['outdoor'], both: ['indoor', 'outdoor'] };
export const sidesOf = (settingId) => SIDES[settingId] || [];

// Max guests: the largest capacity of the settings the venue has. Text, as today.
export const maxGuestsFrom = (values) => {
  const numbers = CAPACITY_FIELDS.filter((id) => isShown(id, values))
    .map((id) => values[id])
    .filter((n) => typeof n === 'number');
  return numbers.length ? String(Math.max(...numbers)) : '';
};

export const parkingTotal = (values) => PARKING_FIELDS.reduce((sum, id) => sum + (values[id] || 0), 0);

// Old amenities (decision C): filled from the new answers. The rest are checkboxes.
const DERIVED_AMENITIES = {
  restrooms: (v) => RESTROOM_FIELDS.some((id) => v[id] > 0),
  changingRooms: (v) => v['rooms.bridalSuite'] === true || v['rooms.groomsRoom'] === true,
  danceFloor: (v) => v['space.danceArea'] === true,
  wifi: (v) => v['tech.wifi'] === true,
  lights: (v) => v['lighting.outdoor'] === true,
  kitchen: (v) => v.kitchen === 'full' || v.kitchen === 'prep',
  bridesRoom: (v) => v['rooms.bridalSuite'] === true,
  parkingOnSite: (v) => v['parking.onProperty'] > 0,
  pool: (v) => v['facilities.pool'] === true,
  // VS1.4 decision B: from the items marked Included.
  ...Object.fromEntries(Object.entries(dictionary.lists.amenity.inventoryItem).map(([amenity, item]) => [amenity, (v) => hasIncluded(v.inventory, item)]))
};
export const KEPT_AMENITIES = ['runningWater', 'stagingRoom'];
const AMENITY_ORDER = [
  'restrooms',
  'changingRooms',
  'danceFloor',
  'wifi',
  'tables',
  'lights',
  'kitchen',
  'chairs',
  'bridesRoom',
  'parkingOnSite',
  'pool',
  'runningWater',
  'linens',
  'stagingRoom'
];

export const amenitiesFrom = (values, storedLabels = []) =>
  AMENITY_ORDER.filter((id) => (DERIVED_AMENITIES[id] ? DERIVED_AMENITIES[id](values) : storedLabels.includes(toLegacy('amenity', id)))).map((id) =>
    toLegacy('amenity', id)
  );

// Letters a person sees: no tags, entities as 1 letter, an emoji as 1.
export const visibleLength = (html) => {
  const text = String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-z]+;|&#\d+;/gi, '_')
    .trim();
  if (typeof Intl !== 'undefined' && Intl.Segmenter) return [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text)].length;
  return Array.from(text).length;
};

export const MIN_PHOTOS = 6;

// Everything the host must fix before a save. { key: problem }.
// Keys are field IDs for dictionary fields, and form names for the rest.
export const venueProblems = (form, { needsRequirements, requirementsChecked } = {}) => {
  const values = ruleValues(form);
  const problems = fieldProblems(FACT_FIELDS, values);

  [
    ['name', 'title'],
    ['description', 'description'],
    ['additionalNotes', 'additionalNotes']
  ].forEach(([name, id]) => {
    const limit = maxLength(id);
    if (limit && visibleLength(form[name]) > limit) problems[name] = 'tooLong';
  });

  if (values['rules.eventTypesNotAllowed'] === undefined) {
    if (form.eventTypesRestricted === undefined) problems['rules.eventTypesNotAllowed'] = 'missing';
    else if (form.eventTypesRestricted === true) problems['rules.eventTypesNotAllowed'] = 'pickOne';
  }
  const restricted = values['rules.eventTypesNotAllowed'];
  if (restricted && restricted.list && !String(restricted.other || '').trim()) problems['rules.eventTypesNotAllowed.other'] = 'missing';

  if (!cleanContact({ name: form.contactName, phone: form.contactPhone })) {
    problems['contact.onSite'] = !String(form.contactPhone || '').trim() || !String(form.contactName || '').trim() ? 'missing' : 'badPhone';
  }
  Object.assign(problems, inventoryProblems(values.inventory));
  if (!Array.isArray(form.photos) || form.photos.length < MIN_PHOTOS) problems.photos = 'missing';
  if (needsRequirements && !requirementsChecked) problems.requirements = 'missing';
  return problems;
};

// The public facts to save. Shown and answered fields only; the rest go.
// create: a nested object (addDoc). update: dotted paths, `remove` for the rest.
const savedFacts = (form) => {
  const values = ruleValues(form);
  const facts = {};
  FACT_FIELDS.forEach((id) => {
    if (id === 'parking.total' || id === 'rentals.offered') return;
    if (id === 'inventory') {
      const rows = savedInventory(values.inventory, form.address_country);
      if (rows) facts.inventory = rows;
      return;
    }
    if (isShown(id, values) && isAnswered(id, values[id])) facts[id] = values[id];
  });
  if (facts['overnight.beds']) {
    facts['overnight.beds'] = Object.fromEntries(Object.entries(facts['overnight.beds']).filter(([, count]) => count > 0));
  }
  if (PARKING_FIELDS.every((id) => facts[id] !== undefined)) facts['parking.total'] = parkingTotal(values);
  if (facts.inventory) facts['rentals.offered'] = rentalsOffered(facts.inventory);
  return facts;
};

const nest = (flat) => {
  const out = {};
  Object.entries(flat).forEach(([path, value]) => {
    const keys = path.split('.');
    let node = out;
    keys.slice(0, -1).forEach((key) => {
      node[key] = node[key] || {};
      node = node[key];
    });
    node[keys[keys.length - 1]] = value;
  });
  return out;
};

export const venueFactsWrite = (form, { create, remove }) => {
  const facts = savedFacts(form);
  if (create) return nest(facts);
  const update = {};
  FACT_FIELDS.forEach((id) => {
    update[id] = id in facts ? facts[id] : remove;
  });
  return update;
};

// VS1.6: the house-rules document. A new file: write it with the server time
// (firestore.rules). Removed: delete the field. Unchanged, or a new venue
// with none: leave it alone (a new venue is a set(), where a delete is refused).
// The host's file name is never stored: the venue is public, and a name can
// hold private facts.
export const houseRulesWrite = (form, { create, remove, now }) => {
  const rules = form.houseRules;
  if (rules && rules.isNew) return { houseRules: { path: rules.path, uploadedAt: now } };
  if (!rules && !create) return { houseRules: remove };
  return {};
};

export const currencyFor = (country) => regionOf(country).currency;

// The private record for this venue: users/{hostId}/venuePrivate/{venueId}.
export const privateWrite = (form, { requirementsVersion, userId, now, role }) => {
  const record = {
    contact: { onSite: cleanContact({ name: form.contactName, phone: form.contactPhone }) },
    updatedAt: now,
    updatedBy: { role, userId }
  };
  if (requirementsVersion) record.intake = { requirements: { version: requirementsVersion, at: now, userId } };
  return record;
};
